import { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { inject } from '@angular/core';
import IUser from '../../_interfaces/IUser';
import IRole from '../../_interfaces/IRole';
import ISite from '../../_interfaces/ISite';
import IPatient from '../../_interfaces/IPatients';
import ITreatment from '../../_interfaces/ITreatment';
import IProduct from '../../_interfaces/IProduct';
import IJob from '../../_interfaces/IJob';
import IMarketingChannels from '../../_interfaces/IMarketingChannels';
import ITreatmentCategory from '../../_interfaces/ITreatmentCategory';
import IProductCategory from '../../_interfaces/IProductCategory';
import ICalendar from '../../_interfaces/ICalendar';
import { AdminEntityLoaderService } from '../../_services/admin/admin-entity-loader.service';
import { resolveAdminCustomerId } from '../../_utils/route-param.utils';

function toPositiveInt(value: string | null): number {
  const parsed = +(value || 0);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function readParam(route: ActivatedRouteSnapshot, key: string): number {
  for (const snapshot of [...route.pathFromRoot].reverse()) {
    const rawValue =
      key === 'customers_id'
        ? snapshot.paramMap.get('customer_identifier') || snapshot.paramMap.get(key)
        : snapshot.paramMap.get(key);
    const parsed =
      key === 'customers_id'
        ? resolveAdminCustomerId(rawValue)
        : toPositiveInt(rawValue);
    if (parsed) {
      return parsed;
    }
  }

  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const fallback = readParamFromPath(pathname, key);
  if (fallback) {
    return fallback;
  }

  return 0;
}

function readParamFromPath(pathname: string, key: string): number {
  const patterns: Record<string, RegExp[]> = {
    customers_id: [/\/administration\/([^/]+)/],
    sites_id: [/\/(?:clinics|sites)\/(\d+)/],
    users_id: [/\/users\/(\d+)/],
    roles_id: [/\/roles\/(\d+)/],
    patients_id: [/\/patients\/(\d+)/],
    treatments_id: [/\/treatments\/(\d+)/],
    products_id: [/\/products\/(\d+)/],
    jobs_id: [/\/jobs\/(\d+)/],
    channels_id: [/\/marketing-channels\/(\d+)/],
    category_id: [/\/categories\/(\d+)/],
    calendar_id: [/\/calendars\/(\d+)/],
  };

  for (const regex of patterns[key] || []) {
    const match = pathname.match(regex);
    const parsed =
      key === 'customers_id'
        ? resolveAdminCustomerId(match?.[1] || null)
        : toPositiveInt(match?.[1] || null);
    if (parsed) {
      return parsed;
    }
  }

  return 0;
}

export const adminUserResolver: ResolveFn<IUser | null> = (route) => {
  const loader = inject(AdminEntityLoaderService);
  const customersId = readParam(route, 'customers_id');
  const usersId = readParam(route, 'users_id');
  return loader.loadUser(customersId, usersId);
};

export const adminRoleResolver: ResolveFn<IRole | null> = (route) => {
  const loader = inject(AdminEntityLoaderService);
  const customersId = readParam(route, 'customers_id');
  const rolesId = readParam(route, 'roles_id');
  return loader.loadRole(customersId, rolesId);
};

export const adminClinicResolver: ResolveFn<ISite | null> = (route) => {
  const loader = inject(AdminEntityLoaderService);
  const customersId = readParam(route, 'customers_id');
  const sitesId = readParam(route, 'sites_id');
  return loader.loadClinic(customersId, sitesId);
};

export const adminPatientResolver: ResolveFn<IPatient | null> = (route) => {
  const loader = inject(AdminEntityLoaderService);
  const customersId = readParam(route, 'customers_id');
  const patientsId = readParam(route, 'patients_id');
  return loader.loadPatient(customersId, patientsId);
};

export const adminTreatmentResolver: ResolveFn<ITreatment | null> = (route) => {
  const loader = inject(AdminEntityLoaderService);
  const customersId = readParam(route, 'customers_id');
  const treatmentsId = readParam(route, 'treatments_id');
  return loader.loadTreatment(customersId, treatmentsId);
};

export const adminProductResolver: ResolveFn<IProduct | null> = (route) => {
  const loader = inject(AdminEntityLoaderService);
  const customersId = readParam(route, 'customers_id');
  const productsId = readParam(route, 'products_id');
  return loader.loadProduct(customersId, productsId);
};

export const adminJobResolver: ResolveFn<IJob | null> = (route) => {
  const loader = inject(AdminEntityLoaderService);
  const customersId = readParam(route, 'customers_id');
  const jobsId = readParam(route, 'jobs_id');
  return loader.loadJob(customersId, jobsId);
};

export const adminMarketingResolver: ResolveFn<IMarketingChannels | null> = (
  route,
) => {
  const loader = inject(AdminEntityLoaderService);
  const customersId = readParam(route, 'customers_id');
  const channelsId = readParam(route, 'channels_id');
  return loader.loadMarketingChannel(customersId, channelsId);
};

export const adminTreatmentCategoryResolver: ResolveFn<ITreatmentCategory | null> =
  (route) => {
    const loader = inject(AdminEntityLoaderService);
    const customersId = readParam(route, 'customers_id');
    const categoryId = readParam(route, 'category_id');
    return loader.loadTreatmentCategory(customersId, categoryId);
  };

export const adminProductCategoryResolver: ResolveFn<IProductCategory | null> = (
  route,
) => {
  const loader = inject(AdminEntityLoaderService);
  const customersId = readParam(route, 'customers_id');
  const categoryId = readParam(route, 'category_id');
  return loader.loadProductCategory(customersId, categoryId);
};

export const adminCalendarResolver: ResolveFn<ICalendar | null> = (route) => {
  const loader = inject(AdminEntityLoaderService);
  const customersId = readParam(route, 'customers_id');
  const sitesId = readParam(route, 'sites_id');
  const calendarId = readParam(route, 'calendar_id');
  return loader.loadCalendar(customersId, sitesId, calendarId);
};
